import { BodyRegular, Card, Headline3, Headline4 } from 'components/Elements';
import ExpandableContentComponent from 'components/ExpandableContentComponent';
import { ErrorNotifier } from 'components/notifiers/ErrorNotifier';
import useGetRequest from 'hooks/useGetRequest';
import { OrganisationBilling, MonthlyBill, CourseBill, EnrollmentBill } from 'models/OrganisationBilling';
import { styled } from 'styled-components';
import { breakpoints, sizes, theme } from 'theme';
import strings from '../../../strings/strings.json';

type BillingListBaseProps = {
    children?: React.ReactNode;
};

function BillingListBase({ children }: BillingListBaseProps) {
    return (
        <BillingListContainer>
            <Headline3>{strings.billing.title}</Headline3>
            {children}
        </BillingListContainer>
    );
}

export default function BillingList(): JSX.Element {
    const { data: organisationBills, loading, errors } = useGetRequest<OrganisationBilling[]>('billing/all');

    if (loading) return <BillingListBase />;

    if (errors.length)
        return (
            <BillingListBase>
                <ErrorNotifier title={strings.billing.errorTitle} description={strings.billing.errorDescription} />
            </BillingListBase>
        );

    return (
        <BillingListBase>
            {organisationBills &&
                organisationBills.map((organisationBill, index) => {
                    return (
                        <OrganisationCard key={index}>
                            <Headline4>{organisationBill.organisationName}</Headline4>

                            {organisationBill.monthlyBills.map((monthlyBill, monthlyBillIndex) => {
                                return <BillCard key={monthlyBillIndex} monthlyBill={monthlyBill} />;
                            })}
                        </OrganisationCard>
                    );
                })}
        </BillingListBase>
    );
}

export function OrganisationBill(): JSX.Element {
    const { data: bill, loading, errors } = useGetRequest<OrganisationBilling>(`billing`);

    if (loading) return <BillingListBase />;

    if (errors.length)
        return (
            <BillingListBase>
                <ErrorNotifier title={strings.billing.errorTitle} description={strings.billing.errorDescription} />
            </BillingListBase>
        );

    if (!bill) return <></>;

    return (
        <BillingListBase>
            <BillCardList>
                {bill.monthlyBills.map((mb) => (
                    <BillCard monthlyBill={mb} />
                ))}
            </BillCardList>
        </BillingListBase>
    );
}

const BillCardList = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${sizes.spacingMd};
`;

type BillCardProps = {
    monthlyBill: MonthlyBill;
};

export function BillCard({ monthlyBill }: BillCardProps): JSX.Element {
    const totalCoursesFee = monthlyBill.courseBills.reduce((sum, bill) => sum + bill.courseFee, 0);
    const enrolledLearnersFee = monthlyBill.enrollmentBills.reduce((sum, bill) => sum + bill.enrollmentsFee, 0);
    const numberOfEnrolledLearners = monthlyBill.enrollmentBills.reduce(
        (sum, bill) => sum + bill.numberOfEnrollments,
        0,
    );

    const monthTotal = totalCoursesFee + enrolledLearnersFee + monthlyBill.subscriptionFee;

    const header = (
        <MainRow>
            <Cell>
                <BodyRegular $bold>{strings.billing.mainRow.month}</BodyRegular>
                <BodyRegular>{`${new Date(monthlyBill.month).toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })}`}</BodyRegular>
            </Cell>
            <Cell>
                <BodyRegular $bold>{strings.billing.mainRow.subscription}</BodyRegular>
                <BodyRegular>{`£${monthlyBill.subscriptionFee}`}</BodyRegular>
            </Cell>
            <Cell>
                <BodyRegular $bold>{strings.billing.mainRow.liveCourses}</BodyRegular>
                <BodyRegular>{`£${totalCoursesFee + enrolledLearnersFee} (${monthlyBill.courseBills.length})`}</BodyRegular>
            </Cell>
            <Cell>
                <BodyRegular $bold>{strings.billing.mainRow.enrollments}</BodyRegular>
                <BodyRegular>{`£${enrolledLearnersFee} (${numberOfEnrolledLearners})`}</BodyRegular>
            </Cell>
            <Cell>
                <BodyRegular $bold>{strings.billing.mainRow.total}</BodyRegular>
                <BodyRegular>{`£${monthTotal}`}</BodyRegular>
            </Cell>
        </MainRow>
    );

    return (
        <Card>
            <CardBody>
                <ExpandableContentComponent
                    headline={header}
                    content={
                        <CourseList
                            courseBills={monthlyBill.courseBills}
                            enrollmentBills={monthlyBill.enrollmentBills}
                        />
                    }
                    closedHeightInPx={0}
                />
            </CardBody>
        </Card>
    );
}

const CardBody = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${sizes.spacingMd};
`;

const MainRow = styled.div`
    display: grid;
    width: 75%;
    grid-template-columns: repeat(5, 1fr);

    @media (max-width: ${breakpoints.lg}) {
        width: 100%;
    }

    @media (max-width: ${breakpoints.md}) {
        grid-template-columns: repeat(3, 1fr);
        row-gap: ${sizes.spacingSm};
    }

    @media (max-width: ${breakpoints.sm}) {
        grid-template-columns: repeat(2, 1fr);
    }

    @media (max-width: ${breakpoints.xs}) {
        grid-template-columns: 1fr;
    }
`;

const Cell = styled.div`
    display: flex;
    flex-direction: column;
`;

const BillingListContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${sizes.spacingRg};
`;

const OrganisationCard = styled.div`
    display: flex;
    flex-direction: column;
`;

type CourseListProps = {
    courseBills: CourseBill[];
    enrollmentBills: EnrollmentBill[];
};

function CourseList({ courseBills, enrollmentBills }: CourseListProps): JSX.Element {
    let itemNumber = 1;

    return (
        <CourseListContainer>
            {courseBills.map((courseBill, index) => (
                <CourseItem key={index}>
                    <CourseTitleRow>
                        <BodyRegular $bold>{`${itemNumber++}. ${courseBill.courseTitle}`}</BodyRegular>
                    </CourseTitleRow>
                    <BreakdownRow>
                        <BreakdownColumn>
                            <BodyRegular $bold>{strings.billing.fields.dateCreated}</BodyRegular>
                            <BodyRegular>{`${new Date(courseBill.courseCreateDate).toLocaleDateString()}`}</BodyRegular>
                        </BreakdownColumn>
                        <BreakdownColumn>
                            <BodyRegular $bold>{strings.billing.fields.courseFee}</BodyRegular>
                            <BodyRegular>{`£${courseBill.courseFee}`}</BodyRegular>
                        </BreakdownColumn>
                    </BreakdownRow>
                </CourseItem>
            ))}
            {enrollmentBills.map((enrollmentBill, index) => (
                <CourseItem key={index}>
                    <CourseTitleRow>
                        <BodyRegular $bold>{`${itemNumber++}. ${enrollmentBill.courseTitle}`}</BodyRegular>
                    </CourseTitleRow>
                    <BreakdownRow>
                        <BreakdownColumn>
                            <BodyRegular $bold>{strings.billing.fields.dateCreated}</BodyRegular>
                            <BodyRegular>{`${new Date(enrollmentBill.courseCreateDate).toLocaleDateString()}`}</BodyRegular>
                        </BreakdownColumn>
                        <BreakdownColumn>
                            <BodyRegular $bold>{strings.billing.fields.enrollmentsFee}</BodyRegular>
                            <BodyRegular>{`£${enrollmentBill.enrollmentsFee} (+${enrollmentBill.numberOfEnrollments} ${strings.billing.fields.learner})`}</BodyRegular>
                        </BreakdownColumn>
                    </BreakdownRow>
                </CourseItem>
            ))}
        </CourseListContainer>
    );
}

const CourseTitleRow = styled.div`
    display: flex;
    gap: ${sizes.spacingSm};
    width: 100%;
`;

const BreakdownColumn = styled.div`
    display: flex;
    flex-direction: column;
`;

const BreakdownRow = styled.div`
    display: flex;
    gap: ${sizes.spacingXl};

    @media (max-width: ${breakpoints.xs}) {
        flex-direction: column;
        gap: ${sizes.spacingXs};
    }
`;

const CourseItem = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${sizes.spacingSm};
    border-top: 1px solid ${theme.cardBorder};
    padding-top: ${sizes.spacingMd};
`;

const CourseListContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${sizes.spacingMd};
`;
